import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Home, LayoutGrid, Search, RotateCcw, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Mobile-only bottom navigation (storefront). Mirrors the design blueprint:
 * Home · Categories · Search · Reorder · Account — amber active state,
 * safe-area aware, hidden on md+ screens and on /admin routes (mounted
 * conditionally in App.js).
 */
export default function MobileBottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const focusSearch = pathname === "/shop" && searchParams.get("focus") === "search";

  const items = [
    { id: "home", label: "Home", icon: Home, to: "/", active: pathname === "/" },
    { id: "categories", label: "Categories", icon: LayoutGrid, to: "/shop", active: pathname === "/shop" && !focusSearch },
    { id: "search", label: "Search", icon: Search, to: "/shop?focus=search", active: focusSearch },
    { id: "reorder", label: "Reorder", icon: RotateCcw, to: "/orders#buy-again", active: pathname === "/orders" },
    {
      id: "account",
      label: user ? "Account" : "Sign In",
      icon: UserRound,
      to: user ? "/account/business" : "/login",
      active: pathname.startsWith("/account") || pathname === "/login" || pathname === "/wishlist",
    },
  ];

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label="Mobile navigation"
      className="bg-nav fixed inset-x-0 bottom-0 z-[60] border-t border-line backdrop-blur-xl md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid grid-cols-5">
        {items.map((item) => (
          <button
            key={item.id}
            data-testid={`bottom-nav-${item.id}`}
            onClick={() => navigate(item.to)}
            className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold transition-colors ${
              item.active ? "text-amber-500" : "text-3 hoverable-t1"
            }`}
            aria-current={item.active ? "page" : undefined}
          >
            <item.icon className="h-5 w-5" strokeWidth={item.active ? 2.2 : 1.8} />
            {item.label}
            <span className={`h-0.5 w-6 rounded-full transition-colors ${item.active ? "bg-amber-500" : "bg-transparent"}`} />
          </button>
        ))}
      </div>
    </nav>
  );
}
